import { useMutation, useQueryClient } from "react-query";
import { AxiosError, isAxiosError } from "axios";
import { useErrorBoundary } from "component-library";
import { authAxios as axios } from "api-generate-types/src/axios";
import { HALManager, UserManager } from "app/core";
import type { ProfileIdentityPutBody, ProfileIdentityResponse } from "models";
import {
  GET_PRONOUNS_PRONUNCIATION_QUERY_KEY,
  type IPronounsPronunciation,
} from "pages/Profile/hooks/useGetPronounsPronunciation";
import { getApiUrl } from "services/UrlBuilder";

export interface IUpdatePronounsPronunciationBody {
  pronouns: string;
  pronunciation: string;
}

function isValidationErrorPayload(data: unknown): boolean {
  if (!Array.isArray(data) || data.length === 0) {
    return false;
  }

  return data.every(
    (x) =>
      typeof x === "object" &&
      x !== null &&
      typeof (x as { description?: unknown }).description === "string"
  );
}

export function useUpdatePronounsPronunciation() {
  const queryClient = useQueryClient();
  const captureError = useErrorBoundary();

  return useMutation<
    ProfileIdentityResponse,
    AxiosError,
    IUpdatePronounsPronunciationBody
  >(
    async (body) => {
      const profileId = String(UserManager.userId);
      const payload: ProfileIdentityPutBody = {
        Pronouns: body.pronouns,
        Pronunciation: body.pronunciation,
      };

      const requestUrl = getApiUrl(HALManager.getProfileIdentityUrl(profileId));

      const { data } = await axios.put<ProfileIdentityResponse>(
        requestUrl,
        payload
      );

      return data;
    },
    {
      onSuccess: (response) => {
        const next: IPronounsPronunciation = {
          pronouns: response.pronouns ?? "",
          pronunciation: response.pronunciation ?? "",
        };

        const uid = String(UserManager.userId);

        queryClient.setQueryData(
          [GET_PRONOUNS_PRONUNCIATION_QUERY_KEY, uid, false],
          next
        );
        void queryClient.invalidateQueries(
          GET_PRONOUNS_PRONUNCIATION_QUERY_KEY
        );
      },
      onError: (error) => {
        if (
          isAxiosError(error) &&
          error.response?.status === 400 &&
          isValidationErrorPayload(error.response.data)
        ) {
          return;
        }

        captureError(error);
      },
    }
  );
}
